'use client'

import { useState, useCallback, useRef, useEffect } from 'react'
import { fetchSidebarData, type SidebarNews, type SidebarData } from '@/services/sidebarApi'
import { useRouter } from '@/i18n/navigation'
import { useLocale } from 'next-intl'
import { t } from '@/i18n/translations'

/**
 * U2 (Temmuz 2026 denetimi): bu şerit `ssr: false` ile yükleniyordu; ilk HTML'de
 * yalnızca boş kırmızı bir kutu vardı ve içerik JS çalışınca doluyordu.
 * Artık layout veriyi sunucuda çekip `initialData` olarak veriyor — başlıklar ham
 * HTML'de hazır geliyor, useEffect yalnızca tazeleme için çalışıyor.
 */
type Props = { initialData?: SidebarData | null }

export default function BreakingNews({ initialData }: Props) {
  const lang = useLocale()
  const router = useRouter()
  const initialNews = initialData?.news ?? []
  const [items, setItems] = useState<SidebarNews[]>(initialNews)
  const [headlines, setHeadlines] = useState<string[]>(
    initialNews.length > 0 ? initialNews.map(n => n.title) : [t('status.loadingMarket', lang)],
  )
  const [current, setCurrent] = useState(0)
  const [displayed, setDisplayed] = useState(0)
  const [phase, setPhase] = useState<'visible' | 'exit' | 'enter'>('visible')
  const [direction, setDirection] = useState<'left' | 'right'>('left')
  const touchStartX = useRef(0)
  const transitioning = useRef(false)
  const headlinesRef = useRef(headlines)
  headlinesRef.current = headlines

  /* ── Sidebar API'dan haberleri çek ── */
  useEffect(() => {
    const load = async () => {
      const data = await fetchSidebarData(lang)
      if (data && data.news.length > 0) {
        setItems(data.news)
        setHeadlines(data.news.map(n => n.title))
        setCurrent(0)
        setDisplayed(0)
      }
    }
    load()
    const iv = setInterval(load, 5 * 60_000)
    return () => clearInterval(iv)
  }, [lang])

  const goTo = useCallback((idx: number, dir: 'left' | 'right') => {
    if (transitioning.current) return
    transitioning.current = true
    setDirection(dir)
    setCurrent(idx)

    // 1. Eski metin çıksın
    setPhase('exit')

    // 2. 300ms sonra yeni metin gelsin
    setTimeout(() => {
      setDisplayed(idx)
      setPhase('enter')

      // 3. 50ms sonra görünür yap (animasyon tetiklensin)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setPhase('visible')
          setTimeout(() => {
            transitioning.current = false
          }, 400)
        })
      })
    }, 350)
  }, [])

  const next = useCallback(() => {
    goTo((current + 1) % headlinesRef.current.length, 'left')
  }, [current, goTo])

  const prev = useCallback(() => {
    goTo((current - 1 + headlinesRef.current.length) % headlinesRef.current.length, 'right')
  }, [current, goTo])

  // Otomatik geçiş
  useEffect(() => {
    if (headlines.length <= 1) return
    const interval = setInterval(() => {
      goTo((current + 1) % headlines.length, 'left')
    }, 6000)
    return () => clearInterval(interval)
  }, [current, goTo, headlines.length])

  // Mobilde swipe
  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX
  }

  const onTouchEnd = (e: React.TouchEvent) => {
    const diff = touchStartX.current - e.changedTouches[0].clientX
    if (Math.abs(diff) > 30) {
      if (transitioning.current) return
      if (diff > 0) next()
      else prev()
    }
  }

  const getStyle = (): React.CSSProperties => {
    if (phase === 'exit') {
      return {
        opacity: 0,
        transform: direction === 'left' ? 'translateX(-40px)' : 'translateX(40px)',
        transition: 'opacity 0.35s ease, transform 0.35s ease',
      }
    }
    if (phase === 'enter') {
      return {
        opacity: 0,
        transform: direction === 'left' ? 'translateX(40px)' : 'translateX(-40px)',
        transition: 'none',
      }
    }
    return {
      opacity: 1,
      transform: 'translateX(0)',
      transition: 'opacity 0.4s ease, transform 0.4s ease',
    }
  }

  return (
    <div
      className="flex items-center h-10 select-none"
      style={{ backgroundColor: '#A81E1E', touchAction: 'pan-y', WebkitTouchCallout: 'none' }}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="max-w-7xl mx-auto px-2 md:px-4 flex items-center w-full gap-2 md:gap-3">
        {/* SON DAKİKA etiketi */}
        <span className="shrink-0 text-white font-black text-[10px] md:text-[13px] tracking-wider">
          {lang === 'en' ? 'BREAKING' : 'SON DAKİKA'}
        </span>

        {/* Ayırıcı nokta - dalga efektli */}
        <span className="shrink-0 relative flex items-center justify-center w-4 h-4">
          <span className="absolute w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-white opacity-80" />
          <span className="absolute w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-white animate-ping opacity-40" />
        </span>

        {/* Haber metni */}
        <div
          className="flex-1 overflow-hidden min-h-[1.25rem] cursor-pointer"
          onClick={() => {
            const item = items[displayed]
            if (item?.slug) router.push({ pathname: '/news/[slug]', params: { slug: item.slug } })
          }}
        >
          <p
            className="text-white text-[11px] md:text-[13px] font-medium m-0 leading-relaxed truncate md:whitespace-normal md:overflow-visible md:text-clip hover:underline"
            style={getStyle()}
          >
            {headlines[displayed]}
          </p>
        </div>

        {/* Ok butonları */}
        {headlines.length > 1 && (
          <div className="hidden md:flex items-center shrink-0 ml-2 gap-1">
            <button
              onClick={prev}
              aria-label={t('aria.prevNews', lang)}
              className="w-8 h-12 flex items-center justify-center border-none cursor-pointer transition-opacity duration-200 hover:opacity-60"
              style={{ background: 'transparent', color: 'white' }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <button
              onClick={next}
              aria-label={t('aria.nextNews', lang)}
              className="w-8 h-12 flex items-center justify-center border-none cursor-pointer transition-opacity duration-200 hover:opacity-60"
              style={{ background: 'transparent', color: 'white' }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
